import { useCallback, useEffect, useState } from "react";
import {
  applyEntity,
  createSelection,
  removeEntity,
} from "@sonder/editor/source/core/helpers";
import { useEditorStore } from "@sonder/editor/source/core/stores";
import {
  SearchTermState,
  cancelSearchTermAction,
  decrementSearchTermLengthAction,
  incrementSearchTermLengthAction,
  startSearchTermAction,
  updateSearchTermAction,
  updateSearchTermEntityKeyAction,
  useSearchTermStore,
} from "@sonder/editor/source/core/stores/SearchTermStore";

const VALID_SEARCH_CHARACTERS = /^[0-9a-zA-Z!@.\s]$/;

const getSearchTermSelection = (searchTermState: SearchTermState) => {
  const { startingSelection, searchTermLength } = searchTermState;

  if (!startingSelection) return null;

  return createSelection({
    anchorKey: startingSelection.getStartKey(),
    anchorOffset: startingSelection.getStartOffset(),
    focusKey: startingSelection.getEndKey(),
    focusOffset: startingSelection.getStartOffset() + searchTermLength,
    hasFocus: true,
    isBackward: false,
  });
};

const getDecrementAmount = (
  editorState: Draft.EditorState,
  deferredSelection?: Draft.SelectionState,
) => {
  const selection = deferredSelection ?? editorState.getSelection();
  if (selection.isCollapsed()) return 1;
  return selection.getEndOffset() - selection.getStartOffset();
};

type UseSearchTerm = () => {
  searchTerm: string | null;
  searchTermEntityKey: string | null;
  cancelSearchTerm: () => void;
  onKeyboardInput: (e: React.KeyboardEvent) => void;
};
export const useSearchTerm: UseSearchTerm = () => {
  // Get the current editor, content, block, and selection state
  const { state: editorState, setState: setEditorState } = useEditorStore();
  const { state: searchTermState, dispatch } = useSearchTermStore();
  const { searchTerm, startingSelection, isSelectionInSearchTerm, entityKey } =
    searchTermState;

  const [deferredEntity, setDeferredEntity] = useState(false);
  useEffect(() => {
    if (deferredEntity) {
      const searchTermSelection = getSearchTermSelection(searchTermState);
      if (searchTermSelection && isSelectionInSearchTerm) {
        const { nextEditorState, nextEntityKey } = applyEntity(
          editorState,
          searchTermSelection,
          {
            type: "SEARCH_TERM",
            mutability: "MUTABLE",
            data: { searchTerm: searchTermState.searchTerm },
          },
        );
        dispatch(updateSearchTermEntityKeyAction(nextEntityKey));
        setEditorState(nextEditorState);
      }
      setDeferredEntity(false);
    }
  }, [deferredEntity]);

  // Cancel the search term if the user deletes the "@" symbol
  useEffect(() => {
    if (!searchTerm?.startsWith("@")) {
      dispatch(cancelSearchTermAction());
      setEditorState(removeEntity(editorState, entityKey));
    }
  }, [searchTerm]);

  // Cancel search term when selection leaves the current block
  useEffect(() => {
    if (startingSelection && !isSelectionInSearchTerm) {
      dispatch(cancelSearchTermAction());
      setEditorState(removeEntity(editorState, entityKey));
    }
  }, [isSelectionInSearchTerm]);

  // Track search term length so we can detect search term cancellation
  const [deferredKey, setDeferredKey] = useState<string>();
  const [deferredSelection, setDeferredSelection] =
    useState<Draft.SelectionState>();
  useEffect(() => {
    dispatch(updateSearchTermAction(editorState));
    if (deferredKey) {
      // Decrement search term length on backspace
      if (deferredKey === "Backspace" && startingSelection) {
        const decrementAmount = getDecrementAmount(
          editorState,
          deferredSelection,
        );
        dispatch(decrementSearchTermLengthAction(editorState, decrementAmount));
      }
      // Decrement search term length on delete
      else if (deferredKey === "Delete" && startingSelection) {
        const decrementAmount = getDecrementAmount(
          editorState,
          deferredSelection,
        );
        dispatch(decrementSearchTermLengthAction(editorState, decrementAmount));
      }
      // Increment search term length on valid character input
      else if (VALID_SEARCH_CHARACTERS.test(deferredKey) && startingSelection)
        dispatch(incrementSearchTermLengthAction(editorState));

      setDeferredKey(undefined);
      setDeferredEntity(true);
    }
  }, [editorState]);

  // Keyboard input handler
  const onKeyboardInput = useCallback(
    (e: React.KeyboardEvent) => {
      // Start search term tracking when user types "@"
      if (e.key === "@" && !startingSelection)
        dispatch(startSearchTermAction(editorState));
      // Cancel search term tracking when user presses "Escape"
      else if (e.key === "Escape" && startingSelection) {
        dispatch(cancelSearchTermAction());
        setEditorState(removeEntity(editorState, entityKey));
      }
      // Defer key event until after editor state has been updated
      else if (!e.metaKey && !e.ctrlKey) {
        setDeferredKey(e.key);
        setDeferredSelection(editorState.getSelection());
      }
    },
    [editorState, startingSelection],
  );

  const cancelSearchTerm = useCallback(() => {
    dispatch(cancelSearchTermAction());
    setEditorState(removeEntity(editorState, entityKey));
  }, [editorState, entityKey]);

  return {
    searchTerm,
    searchTermEntityKey: entityKey,
    cancelSearchTerm,
    onKeyboardInput,
  };
};
